import api from '../../../api';

const API_URL = '/crm/expenses';

/**
 * Get expenses with optional date filters and pagination (backend returns Page<T>)
 * @param {Object} params - { dateFrom?, dateTo?, branchId?, page?, size? }
 * @returns {Promise<{ content: Array, totalElements: number, totalPages: number }>}
 */
export const getExpenses = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.set('page', String(params.page ?? 0));
    queryParams.set('size', String(params.size ?? 10));
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params.branchId) queryParams.append('branchId', params.branchId);

    const url = `${API_URL}?${queryParams}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching expenses:', error);
    throw error;
  }
};

/**
 * Get expenses by project ID
 */
export const getExpensesByProjectId = async (projectId) => {
  try {
    const response = await api.get(`${API_URL}/by-project/${projectId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching expenses by project:', error);
    throw error;
  }
};

/**
 * Get expense by ID
 */
export const getExpenseById = async (id) => {
  try {
    const response = await api.get(`${API_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching expense:', error);
    throw error;
  }
};

/**
 * Create expense (with multipart/form-data for file upload)
 */
export const createExpense = async (expenseData) => {
  try {
    const formData = new FormData();
    
    // Prepare expense request object
    const code = (expenseData.currencyCode || 'IDR').toUpperCase();
    const expenseRequest = {
      expenseDate: expenseData.expenseDate ? new Date(expenseData.expenseDate).toISOString().split('T')[0] : null,
      categoryId: expenseData.categoryId ? parseInt(expenseData.categoryId) : null,
      description: expenseData.description || '',
      amount: expenseData.amount ? parseFloat(expenseData.amount) : null,
      currencyCode: code,
      exchangeRateAtCreation: expenseData.exchangeRateAtCreation != null
        ? parseFloat(expenseData.exchangeRateAtCreation)
        : (code === 'IDR' ? 1 : null),
      companyId: expenseData.companyId ? parseInt(expenseData.companyId) : null,
      projectId: expenseData.projectId ? parseInt(expenseData.projectId) : null
    };

    // Add JSON data as "data" part
    formData.append('data', new Blob([JSON.stringify(expenseRequest)], {
      type: 'application/json'
    }));

    // Add file if any
    if (expenseData.documents && expenseData.documents.length > 0) {
      const file = expenseData.documents[0].file;
      if (file) {
        formData.append('file', file);
      }
    }
    
    const response = await api.post(API_URL, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error creating expense:', error);
    throw error;
  }
};

/**
 * Update expense (with multipart/form-data for file upload)
 */
export const updateExpense = async (id, expenseData) => {
  try {
    const formData = new FormData();
    
    // Prepare expense request object
    const codeUpdate = (expenseData.currencyCode || 'IDR').toUpperCase();
    const expenseRequest = {
      expenseDate: expenseData.expenseDate ? new Date(expenseData.expenseDate).toISOString().split('T')[0] : null,
      categoryId: expenseData.categoryId ? parseInt(expenseData.categoryId) : null,
      description: expenseData.description || '',
      amount: expenseData.amount ? parseFloat(expenseData.amount) : null,
      currencyCode: codeUpdate,
      exchangeRateAtCreation: expenseData.exchangeRateAtCreation != null
        ? parseFloat(expenseData.exchangeRateAtCreation)
        : (codeUpdate === 'IDR' ? 1 : null),
      companyId: expenseData.companyId ? parseInt(expenseData.companyId) : null,
      projectId: expenseData.projectId ? parseInt(expenseData.projectId) : null
    };

    // Add JSON data as "data" part
    formData.append('data', new Blob([JSON.stringify(expenseRequest)], {
      type: 'application/json'
    }));

    // Add file if any (only new files, not existing ones)
    if (expenseData.documents && expenseData.documents.length > 0) {
      const newFiles = expenseData.documents.filter(doc => !doc.isExisting && doc.file);
      if (newFiles.length > 0) {
        formData.append('file', newFiles[0].file);
      }
    }
    
    const response = await api.put(`${API_URL}/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error updating expense:', error);
    throw error;
  }
};

/**
 * Delete expense
 */
export const deleteExpense = async (id) => {
  try {
    const response = await api.delete(`${API_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting expense:', error);
    throw error;
  }
};

/**
 * Download expense file
 */
export const downloadExpenseFile = async (id) => {
  try {
    const response = await api.get(`${API_URL}/download/${id}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error downloading expense file:', error);
    throw error;
  }
};

